import CircularProgress from "@material-ui/core/CircularProgress";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import { ANFORDERUNGART_BEHOERDLICHE_FESTLEGUNG, ANFORDERUNGART_SELBSTUEBERWACHUNGSAUFLAGE } from "../../../consts/anforderungart";
import { PARAMETERART_FRACHT, PARAMETERART_KONZENTRATION, PARAMETERART_MENGE } from "../../../consts/anforderungParameterart";
import { buildDefaultBehoerdlicheFestlegungColumns } from "../../../initialObjects/BescheidObjects";
import { getAnforderungenAusTagesbescheid } from "../../../services/BescheidsService";
import { useHttp } from "../../../services/HttpService";
import { createSelectRowComponentColumn } from "../../../utils/ColumnUtils";
import EAButton from "../../EAButton";
import EAButtonBar from "../../EAButtonBar";
import EADialog from "../../EADialog";
import EATabs from "../../EATabs";
import CollapsibleTable from "../../tableComponents/CollapsibleTable";
import EADatePicker from "../../EADatePicker";

const festlegungenTab = "Festlegungen";
const selbstueberwachungsTab = "Selbstüberwachung";

const emptySelectedIds = () => {
    return {
        [ANFORDERUNGART_BEHOERDLICHE_FESTLEGUNG]: {
            [PARAMETERART_KONZENTRATION]: [],
            [PARAMETERART_FRACHT]: [],
            [PARAMETERART_MENGE]: [],
        },
        [ANFORDERUNGART_SELBSTUEBERWACHUNGSAUFLAGE]: {
            [PARAMETERART_KONZENTRATION]: [],
            [PARAMETERART_FRACHT]: [],
            [PARAMETERART_MENGE]: [],
        },
    };
};

export default function VorbescheidAnforderungenAuswahlDlg({ messstellenId, bescheid, onClose, onAuswahlAusVorbescheidenConfirm }) {
    const [selectedTab, setSelectedTab] = useState(festlegungenTab);
    const [selectedIds, setSelectedIds] = useState(emptySelectedIds());
    const [tagesbescheidDatum, setTagesbescheidDatum] = useState();
    const [volatileTagesbescheidDatum, setVolatileTagesbescheidDatum] = useState(new Date());
    const [{ data: anforderungenAusVorbescheid, loading: anforderungenAusVorbescheidLoading }] = useHttp(
        getAnforderungenAusTagesbescheid(messstellenId, tagesbescheidDatum, bescheid.id),
        {
            manual: !tagesbescheidDatum || !messstellenId || !bescheid,
        }
    );

    useEffect(() => {
        setTagesbescheidDatum(bescheid.bescheidsdatum);
    }, [bescheid.bescheidsdatum]);

    const onSubmit = useCallback(
        (newIds) => {
            const allSelectedIds = [
                ...newIds[ANFORDERUNGART_BEHOERDLICHE_FESTLEGUNG][PARAMETERART_KONZENTRATION],
                ...newIds[ANFORDERUNGART_BEHOERDLICHE_FESTLEGUNG][PARAMETERART_FRACHT],
                ...newIds[ANFORDERUNGART_BEHOERDLICHE_FESTLEGUNG][PARAMETERART_MENGE],
                ...newIds[ANFORDERUNGART_SELBSTUEBERWACHUNGSAUFLAGE][PARAMETERART_KONZENTRATION],
                ...newIds[ANFORDERUNGART_SELBSTUEBERWACHUNGSAUFLAGE][PARAMETERART_FRACHT],
                ...newIds[ANFORDERUNGART_SELBSTUEBERWACHUNGSAUFLAGE][PARAMETERART_MENGE],
            ];
            onAuswahlAusVorbescheidenConfirm(
                tagesbescheidDatum,
                anforderungenAusVorbescheid.filter((a) => allSelectedIds.indexOf(a.id) >= 0)
            );
        },
        [onAuswahlAusVorbescheidenConfirm, anforderungenAusVorbescheid, tagesbescheidDatum]
    );

    const buildTable = useCallback(
        (label, anforderungArt, parameterArt) => {
            if (!anforderungenAusVorbescheid) {
                return null;
            }
            const tblData = anforderungenAusVorbescheid
                ? anforderungenAusVorbescheid.filter((a) => a.anforderungArt.id === anforderungArt && a.parameterArt.id === parameterArt)
                : anforderungenAusVorbescheid;
            const tblSelectedIds = selectedIds[anforderungArt][parameterArt];
            const setTblSelectedIds = (items) => {
                setSelectedIds((oldVal) => {
                    return { ...oldVal, [anforderungArt]: { ...oldVal[anforderungArt], [parameterArt]: items } };
                });
            };

            return (
                <CollapsibleTable
                    id={"anforderungsTbl-" + label}
                    label={label}
                    columns={[
                        createSelectRowComponentColumn("anforderungsTbl-" + label, "id", tblData, tblSelectedIds, setTblSelectedIds),
                        ...buildDefaultBehoerdlicheFestlegungColumns(),
                    ]}
                    tablerest={{ className: "noHorizontalPadding" }}
                    data={tblData}
                    loading={anforderungenAusVorbescheidLoading}
                />
            );
        },
        [anforderungenAusVorbescheidLoading, anforderungenAusVorbescheid, selectedIds]
    );

    return (
        <EADialog
            open={true}
            titleChildren="Anforderungen aus Vorbescheiden übernehmen"
            closeFunction={onClose}
            content={
                <div style={{ minWidth: 850, minHeight: 350 }}>
                    {!tagesbescheidDatum && (
                        <EADatePicker
                            id="tagesbescheidDatum"
                            onChange={setVolatileTagesbescheidDatum}
                            value={volatileTagesbescheidDatum}
                            style={{ width: "100%" }}
                            label="Bitte geben Sie das Datum an, ab welchem die Änderungen an den Anforderungen wirksam werden sollen"
                        />
                    )}
                    {tagesbescheidDatum && anforderungenAusVorbescheidLoading && (
                        <div style={{ display: "flex", justifyContent: "center" }}>
                            <CircularProgress />
                        </div>
                    )}
                    {!anforderungenAusVorbescheidLoading && tagesbescheidDatum && (
                        <Fragment>
                            <EATabs value={selectedTab} onChange={setSelectedTab} options={[festlegungenTab, selbstueberwachungsTab]} />
                            {selectedTab === festlegungenTab && (
                                <div className="gridl">
                                    {buildTable("Konzentration", ANFORDERUNGART_BEHOERDLICHE_FESTLEGUNG, PARAMETERART_KONZENTRATION)}
                                    {buildTable("Menge", ANFORDERUNGART_BEHOERDLICHE_FESTLEGUNG, PARAMETERART_MENGE)}
                                    {buildTable("Fracht", ANFORDERUNGART_BEHOERDLICHE_FESTLEGUNG, PARAMETERART_FRACHT)}
                                </div>
                            )}
                            {selectedTab === selbstueberwachungsTab && (
                                <div className="gridl">
                                    {buildTable("Konzentration", ANFORDERUNGART_SELBSTUEBERWACHUNGSAUFLAGE, PARAMETERART_KONZENTRATION)}
                                    {buildTable("Menge", ANFORDERUNGART_SELBSTUEBERWACHUNGSAUFLAGE, PARAMETERART_MENGE)}
                                    {buildTable("Fracht", ANFORDERUNGART_SELBSTUEBERWACHUNGSAUFLAGE, PARAMETERART_FRACHT)}
                                </div>
                            )}
                        </Fragment>
                    )}
                </div>
            }
            buttons={
                <EAButtonBar>
                    {!tagesbescheidDatum && (
                        <EAButton
                            id="submitTagesbescheiddatum"
                            label="Weiter"
                            onClick={() => setTagesbescheidDatum(volatileTagesbescheidDatum)}
                            color="primary"
                        />
                    )}
                    {tagesbescheidDatum && (
                        <Fragment>
                            <EAButton id="cancelAnforderungenAuswahl" label="Abbrechen" onClick={onClose} />
                            <EAButton
                                id="submitAnforderungenAuswahl"
                                label="Hinzufügen"
                                onClick={() => onSubmit(selectedIds)}
                                color="primary"
                            />
                        </Fragment>
                    )}
                </EAButtonBar>
            }
        />
    );
}
